import React from "react";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon as fa_icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import FormButton from "../../components/form-components/FormButton.js";

const JobStatusModal = ({
    show,
    close,
    jobStatusData,
    currentJobId,
    shouldPoll,
    isLoadingJobStatus
}) => {
    // Helper function to get status badge styling
    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'completed':
                return 'bg-success-soft text-success';
            case 'running':
                return 'bg-primary-soft text-primary';
            case 'failed':
                return 'bg-danger-soft text-danger';
            case 'queued':
                return 'bg-warning-soft text-warning';
            default:
                return 'bg-surface-2 text-text-2';
        }
    }

    const modalStyle = "bg-black bg-opacity-50 z-[9999] fixed grid content-center place-content-center items-center top-0 left-0 right-0 w-full p-4 overflow-x-hidden overflow-y-auto md:inset-0 h-[calc(100%-1rem)] max-h-full cursor-default text-text";

    return ReactDOM.createPortal(
        <>
            {show ? (
                <div className={modalStyle}>
                    <div
                        className="w-[800px] bg-card relative border-card-bd border rounded-card flex flex-col z-[10000]"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        {/* Header */}
                        <div className="flex flex-row justify-between items-start p-4 border-card-bd">
                            <div className="flex flex-col ml-6 mt-2">
                                <div className="text-text-3 uppercase text-sm">Allocation Job</div>
                                <div className="text-text text-2xl font-semibold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                                    Job Status
                                </div>
                            </div>
                            
                            {/* Close button */}
                            <button
                                onClick={() => close()}
                                aria-label="Close dialog"
                                className="cursor-pointer"
                            >
                                <FontAwesomeIcon
                                    icon={fa_icon({ name: "xmark", family: "sharp", style: "solid" })}
                                    className="w-6 h-6 text-text-3 text-[20px] mr-2 mt-1"
                                />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="flex-1 p-6 overflow-y-auto bg-surface-2">
                            <div className="w-full">
                                {/* Job Status Display */}
                                <div className="p-4 bg-card rounded-card border border-card-bd">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-lg font-semibold">Allocation Job Status</h3>
                                        {shouldPoll && (
                                            <div className="flex items-center text-sm text-primary">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                                                Polling active
                                            </div>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <span className="font-medium">Job ID:</span> {currentJobId || 'Waiting for job ID...'}
                                        </div>
                                        <div>
                                            <span className="font-medium">Status:</span> 
                                            {isLoadingJobStatus ? (
                                                <span className="ml-2 px-2 py-1 rounded text-sm bg-primary-soft text-primary flex items-center">
                                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary mr-2"></div>
                                                    Loading...
                                                </span>
                                            ) : jobStatusData && currentJobId ? (
                                                <span className={`ml-2 px-2 py-1 rounded text-sm ${getStatusBadgeClass(jobStatusData.status)}`}>
                                                    {jobStatusData.status}
                                                </span>
                                            ) : shouldPoll && currentJobId ? (
                                                <span className="ml-2 px-2 py-1 rounded text-sm bg-primary-soft text-primary">
                                                    Fetching status...
                                                </span>
                                            ) : (
                                                <span className="ml-2 px-2 py-1 rounded text-sm bg-warning-soft text-warning">
                                                    Initializing...
                                                </span>
                                            )}
                                        </div>
                                        {jobStatusData && currentJobId ? (
                                            <>
                                                <div>
                                                    <span className="font-medium">Started:</span> {new Date(jobStatusData.started_at).toLocaleString()}
                                                </div>
                                                {jobStatusData.completed_at && (
                                                    <div>
                                                        <span className="font-medium">Completed:</span> {new Date(jobStatusData.completed_at).toLocaleString()}
                                                    </div>
                                                )}
                                                {jobStatusData.progress && (
                                                    <div className="col-span-2">
                                                        <span className="font-medium">Progress:</span>
                                                        <div className="mt-1">
                                                            <div className="bg-surface-2 rounded-full h-2">
                                                                <div
                                                                    className="bg-primary h-2 rounded-full transition-all duration-300"
                                                                    style={{ width: `${jobStatusData.progress.percentage || 0}%` }}
                                                                ></div>
                                                            </div>
                                                            <div className="text-sm text-text-2 mt-1">
                                                                {jobStatusData.progress.percentage || 0}% - {jobStatusData.progress.processed_rows || 0} / {jobStatusData.progress.total_rows || 0} rows processed
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                {jobStatusData.error && (
                                                    <div className="col-span-2">
                                                        <span className="font-medium text-danger">Error:</span>
                                                        <div className="text-danger text-sm mt-1">{jobStatusData.error}</div>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="col-span-2">
                                                <div className="text-sm text-text-2">
                                                    Waiting for job to be created and status to be available...
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Debug: Show what data we have */}
                                {jobStatusData && (
                                    <div className="mt-4 p-4 bg-warning-soft rounded-card border border-warning-soft">
                                        <h4 className="text-lg font-semibold text-warning mb-2">Debug Info</h4>
                                        <div className="text-sm">
                                            <div>Status: {jobStatusData.status}</div>
                                            <div>Has Result: {jobStatusData.result ? 'Yes' : 'No'}</div>
                                            <div>Result Keys: {jobStatusData.result ? Object.keys(jobStatusData.result).join(', ') : 'None'}</div>
                                            <div>All Keys: {Object.keys(jobStatusData).join(', ')}</div>
                                        </div>
                                    </div>
                                )}

                                {/* Show results when job is completed */}
                                {jobStatusData && jobStatusData.status === 'completed' && jobStatusData.result && (
                                    <div className="mt-4 p-4 bg-success-soft rounded-card border border-success-soft">
                                        <h4 className="text-lg font-semibold text-success mb-3">Allocation Results</h4>
                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                            <div>
                                                <span className="font-medium">Total Rows Processed:</span> {jobStatusData.result.processed_rows || 0}
                                            </div>
                                            <div>
                                                <span className="font-medium">Successful Rows:</span> {jobStatusData.result.successful_rows || 0}
                                            </div>
                                            <div>
                                                <span className="font-medium">Failed Rows:</span> {jobStatusData.result.failed_rows || 0}
                                            </div>
                                            <div>
                                                <span className="font-medium">Processing Time:</span> {jobStatusData.result.processing_time || 'N/A'}
                                            </div>
                                        </div>
                                        {jobStatusData.result.virtual_tag_results && jobStatusData.result.virtual_tag_results.length > 0 && (
                                            <div className="mt-3">
                                                <h5 className="font-medium text-success mb-2">Virtual Tag Results:</h5>
                                                <div className="space-y-2">
                                                    {jobStatusData.result.virtual_tag_results.map((vtResult, index) => (
                                                        <div key={index} className="bg-card p-3 rounded-card border border-card-bd">
                                                            <div className="font-medium">{vtResult.virtual_tag_name}</div>
                                                            <div className="text-sm text-text-2">
                                                                Matched: {vtResult.summary?.matched_rows || 0} | 
                                                                Unmatched: {vtResult.summary?.unmatched_rows || 0} | 
                                                                Errors: {vtResult.summary?.error_rows || 0}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {!shouldPoll && !currentJobId && (
                                    <div className="mt-4 p-4 bg-card rounded-card border border-card-bd">
                                        <p className="text-text-2">No allocation job in progress. Click "Process Virtual Tags" to start a new job.</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex justify-start items-center py-5 px-4">
                            <FormButton
                                title="Close"
                                style="primary"
                                onClick={() => close()}
                            />
                        </div>
                    </div>
                </div>
            ) : null}
        </>,
        document.body
    );
};

export default JobStatusModal;
